import React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import ForwardIcon from "@mui/icons-material/Forward";
import ReplayIcon from "@mui/icons-material/Replay";
import { Grid, Tooltip } from "@mui/material";
import { RoleBasedRender } from "./RoleBaseRender";

function TableItem({
  dropDown,
  columns,
  rows,
  handleAction,
  showActions,
  isProduction,
  actionRoles,
  user,
}) {
  const getActionLabel = (action) =>
    typeof showActions[action] === "string"
      ? showActions[action]
      : showActions[action]?.label;

  const checkActionCondition = (action, row) =>
    typeof showActions[action] === "string" ||
    !showActions[action]?.condition ||
    showActions[action].condition(row);

  return (
    <Paper elevation={3} sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={{ maxHeight: 500 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {dropDown && <TableCell></TableCell>}
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                hover
                tabIndex={-1}
                key={row.code}
                sx={{ height: 40, "& .MuiTableCell-root": { py: 0.5, px: 1 } }}
              >
                {columns.map((column) => {
                  if (column.id === "action") {
                    const value = row[column.id];
                    return (
                      <TableCell key="actions">
                        <Grid container spacing={1} sx={{ pt: 1.5 }}>
                          {showActions.view &&
                            checkActionCondition("view", row) && (
                              <RoleBasedRender
                                user={user}
                                allowedRoles={actionRoles.view || []}
                              >
                                <Grid item>
                                  <Tooltip title={getActionLabel("view")}>
                                    <IconButton
                                      aria-label="view"
                                      size="small"
                                      onClick={() =>
                                        handleAction("view", value)
                                      }
                                    >
                                      <VisibilityIcon fontSize="inherit" />
                                    </IconButton>
                                  </Tooltip>
                                </Grid>
                              </RoleBasedRender>
                            )}

                          {showActions.edit &&
                            checkActionCondition("edit", row) && (
                              <RoleBasedRender
                                user={user}
                                allowedRoles={actionRoles.edit || []}
                              >
                                <Grid item>
                                  <Tooltip title={getActionLabel("edit")}>
                                    <IconButton
                                      aria-label="edit"
                                      size="small"
                                      onClick={() =>
                                        handleAction("edit", value)
                                      }
                                    >
                                      <EditIcon fontSize="inherit" />
                                    </IconButton>
                                  </Tooltip>
                                </Grid>
                              </RoleBasedRender>
                            )}

                          {showActions.delete &&
                            checkActionCondition("delete", row) && (
                              <RoleBasedRender
                                user={user}
                                allowedRoles={actionRoles.delete || []}
                              >
                                <Grid item>
                                  <Tooltip title={getActionLabel("delete")}>
                                    <IconButton
                                      aria-label="delete"
                                      size="small"
                                      onClick={() =>
                                        handleAction("delete", value)
                                      }
                                    >
                                      <DeleteIcon fontSize="inherit" />
                                    </IconButton>
                                  </Tooltip>
                                </Grid>
                              </RoleBasedRender>
                            )}

                          {showActions.disable &&
                            checkActionCondition("disable", row) && (
                              <RoleBasedRender
                                user={user}
                                allowedRoles={actionRoles.disable || []}
                              >
                                <Grid item>
                                  <Tooltip title={getActionLabel("disable")}>
                                    <IconButton
                                      aria-label="disable"
                                      size="small"
                                      onClick={() =>
                                        handleAction("disable", value)
                                      }
                                    >
                                      <CloseIcon fontSize="inherit" />
                                    </IconButton>
                                  </Tooltip>
                                </Grid>
                              </RoleBasedRender>
                            )}

                          {showActions.production &&
                            checkActionCondition("production", row) && (
                              <>
                                {isProduction ? (
                                  row.warehouseType === "Factory" && (
                                    <RoleBasedRender
                                      user={user}
                                      allowedRoles={
                                        actionRoles.production || []
                                      }
                                    >
                                      <Grid item>
                                        <Tooltip
                                          title={getActionLabel("production")}
                                        >
                                          <IconButton
                                            aria-label="production"
                                            size="small"
                                            onClick={() =>
                                              handleAction("production", value)
                                            }
                                          >
                                            <AddCircleOutlineIcon fontSize="inherit" />
                                          </IconButton>
                                        </Tooltip>
                                      </Grid>
                                    </RoleBasedRender>
                                  )
                                ) : (
                                  <RoleBasedRender
                                    user={user}
                                    allowedRoles={actionRoles.production || []}
                                  >
                                    <Grid item>
                                      <Tooltip
                                        title={getActionLabel("production")}
                                      >
                                        <IconButton
                                          aria-label="production"
                                          size="small"
                                          onClick={() =>
                                            handleAction("production", value)
                                          }
                                        >
                                          <AddCircleOutlineIcon fontSize="inherit" />
                                        </IconButton>
                                      </Tooltip>
                                    </Grid>
                                  </RoleBasedRender>
                                )}
                              </>
                            )}

                          {showActions.settings &&
                            checkActionCondition("settings", row) && (
                              <RoleBasedRender
                                user={user}
                                allowedRoles={actionRoles.settings || []}
                              >
                                <Grid item>
                                  <Tooltip title={getActionLabel("settings")}>
                                    <IconButton
                                      aria-label="settings"
                                      size="small"
                                      onClick={() =>
                                        handleAction("settings", row)
                                      }
                                    >
                                      <SettingsIcon fontSize="inherit" />
                                    </IconButton>
                                  </Tooltip>
                                </Grid>
                              </RoleBasedRender>
                            )}

                          {showActions.saleProduct &&
                            checkActionCondition("saleProduct", row) && (
                              <RoleBasedRender
                                user={user}
                                allowedRoles={actionRoles.saleProduct || []}
                              >
                                <Grid item>
                                  <Tooltip
                                    title={getActionLabel("saleProduct")}
                                  >
                                    <IconButton
                                      aria-label="saleProduct"
                                      size="small"
                                      onClick={() =>
                                        handleAction("saleProduct", row)
                                      }
                                    >
                                      <ForwardIcon fontSize="inherit" />
                                    </IconButton>
                                  </Tooltip>
                                </Grid>
                              </RoleBasedRender>
                            )}

                          {showActions.return &&
                            checkActionCondition("return", row) && (
                              <RoleBasedRender
                                user={user}
                                allowedRoles={actionRoles.return || []}
                              >
                                <Grid item>
                                  <Tooltip title={getActionLabel("return")}>
                                    <IconButton
                                      aria-label="return"
                                      size="small"
                                      onClick={() =>
                                        handleAction("return", value)
                                      }
                                    >
                                      <ReplayIcon fontSize="inherit" />
                                    </IconButton>
                                  </Tooltip>
                                </Grid>
                              </RoleBasedRender>
                            )}
                        </Grid>
                      </TableCell>
                    );
                  } else {
                    const value = row[column.id];
                    return (
                      <TableCell key={column.id} align={column.align}>
                        {column.format && typeof value === "number"
                          ? column.format(value)
                          : value}
                      </TableCell>
                    );
                  }
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default TableItem;
